<template>
  <div class="table-custom-solutions tblcustomsols f-column mw-0 mh-0 w-100">
    <div class="d-flex w-100 no-scroll-track position-relative mw-0">
      <slot name="title"></slot>

      <GwFilter
        v-if="!tcrud_trulySelectedItems.length"
        :asRow="true"
        :hasSaveBtn="false"
        :hasClearBtn="true"
        :hasUrlUpdate="true"
        filterUrlPrefix="ffcs"
        filterConfigKey="customSolutions"
      />

      <TableDeleteBtns
        v-else
        class="no-shrink mt-auto"
        @confirm="tcrud_batchDeleteSelectedItems"
        @close="tcrud_resetSelectedRows()"
      />

      <div class="d-flex no-shrink mb-auto ml-auto">
        <slot name="title_after"></slot>
      </div>
    </div>

    <TableBody
      ref="tablebody"
      :error="tcrud_error"
      :isInfinite="true"
      :items="tcrud_items"
      :loading="tcrud_loading"
      :loadingBottom="tcrud_loading && !!tcrud_offset"
      class="mt-2"
      @update:scroll="handleScroll"
    >
      <template #no_data>
        <div
          class="gw_box flex-1 f-column f-center"
          style="--clr-box-back: var(--clr-black-1)"
        >
          <IconAndText
            v-if="filter.active"
            class="my-auto"
            icon="mdi-filter"
            :title="$t('data_table.custom_solutions.no_items')"
            :subtitle="$t('data_table.custom_solutions.no_data')"
          />
          <IconAndText
            v-else
            class="my-auto"
            icon="mdi-folder-plus"
            :title="$t('data_table.custom_solutions.no_items')"
            :subtitle="$t('data_table.custom_solutions.no_items_sub')"
          >
            <slot name="new_custom_solution_btn"> </slot>
          </IconAndText>
        </div>
      </template>
      <template #head_before>
        <TableHead
          :sortConf="tbl_sortConf"
          :thead="thead"
          class="large-scroll-track"
          @click:th="tbl_handleSort"
        >
          <template #th_before>
            <TableHeadCell
              class="f-center"
              style="
                --overflow-th: none;
                --max-width-th: 6rem;
                --min-width-th: 6rem;
                --padding-th: 0;
                border-top-left-radius: 4px;
              "
            >
              <div
                class="d-flex"
                style="gap: 0.5rem"
              >
                <TableMainCheckbox
                  :indeterminate="tcrud_mainCheckboxValue === 'indeterminate'"
                  :value="tcrud_mainCheckboxValue === 'all'"
                  @click="tcrud_toggleSelectedAll"
                />
                <!-- Key (ffcs_is_favorite) is now hardcoded, after we do the context-table-wrapper, key can be passed from that -->
                <TableFilterStar
                  :value="(filter.params || {}).is_favorite === 'true'"
                  @click="tcrud_resetSelectedRows()"
                  @input="
                    val => $router.replace({ query: { ...$route.query, ffcs_is_favorite: val } })
                  "
                />
                <Checkbox
                  class="csst csst_nm flgmfslctr_title_controls_item"
                  :value="tbl_sortConf.sortKey === 'last_used'"
                  style="
                    --icon-size: 1.25rem;
                    --clr-disabled: var(--clr-grey-2);
                    --clr-enabled: var(--clr-primary);
                  "
                  :icons="[
                    tbl_sortConf.sortOrder === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up',
                    tbl_sortConf.sortOrder === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up',
                  ]"
                  @click="tbl_handleSort({ sortKey: 'last_used' })"
                >
                  <template #content>
                    <div
                      class="csst_content clr-white text-nowrap"
                      style="line-height: 1.2; left: calc(100% + 0.5rem)"
                      v-html="$t(`tooltips.sort_by_last_used_date`)"
                    />
                  </template>
                </Checkbox>
              </div>
            </TableHeadCell>
          </template>
        </TableHead>
      </template>
      <template #default="{ item, index }">
        <CustomSolutionsTableRow
          :thead="thead"
          :item="item"
          tagType="custom_solutions"
          :allTags="allTags"
          :mobile="mobile"
          :index="index"
          :isSelected="!!tcrud_selectedItems[index]"
          :isActive="cussolId === item.id"
          @apply:item="applyItem(item)"
          @toggle:selected="tcrud_toggleSelected(item, index, tcrud_items)"
          @click:tag_item="tag => tcrud_handleTagClick({ item, tag })"
          @patch:name="({ name }) => patchName({ item, name })"
          @toggle:favorite="toggleFavorite(item)"
          @show:detail="
            ({ e, item }) => {
              if ($refs.gwmenu) {
                $refs.gwmenu.toggleOpen(true, e)
                detailItem = item
              }
            }
          "
        >
        </CustomSolutionsTableRow>
      </template>
    </TableBody>

    <GwMenu
      ref="gwmenu"
      placement="right"
      :isMovable="true"
      :isInside="false"
      :zIndexContent="11"
      :useTargetElement="true"
    >
      <template #default="{ methods }">
        <CustomSolutionDetail
          style="--clr-back: var(--clr-black-1-1)"
          class="gw_menu"
          data-gwmenu="noclose"
          :detailId="detailItem.id"
          endpoint="custom-solutions/"
          apiVersion="v4"
          :hasBtns="false"
          @close="methods.toggleOpen(false)"
          @loaded="
            () => {
              if ($refs.gwmenu) {
                $refs.gwmenu.toggleOpen(true)
              }
            }
          "
        />
      </template>
    </GwMenu>
  </div>
</template>

<script>
import GwMenu from '@/components/lib/GwMenu'
import IconAndText from '@/components/Poker/Overlays/IconAndText'
import Checkbox from '@/components/Common/Checkbox/Checkbox'
import TableDeleteBtns from '@/components/DataTables/TableDeleteBtns'
import GwFilter from '@/components/DataTables/Filters/new/GwFilter'
import TableFilterStar from '@/components/DataTables/Filters/TableFilterStar'
import { throttle } from 'lodash'

import TableBody from '@/components/DataTables/v2/TableBody'
import TableHead from '@/components/DataTables/v2/TableHead'
import TableHeadCell from '@/components/DataTables/v2/TableHeadCell'
import table_mix from '@/components/DataTables/table_mix'
import float_mix from '@/mixins/float_mix'
import tcrud_mix from '@/components/DataTables/tcrud_mix'
import CustomSolutionDetail from '@/components/DataTables/v2/tables/CustomSolutionsHistoryList/CustomSolutionDetail'
import CustomSolutionsTableRow from '@/components/DataTables/v2/tables/CustomSolutionsTable/CustomSolutionsTableRow'

export default {
  name: 'CustomSolutionsTable',
  components: {
    TableBody,
    TableHead,
    TableHeadCell,
    CustomSolutionDetail,
    CustomSolutionsTableRow,
    GwMenu,
    IconAndText,
    Checkbox,
    TableDeleteBtns,
    GwFilter,
    TableFilterStar,
  },
  mixins: [tcrud_mix, table_mix, float_mix],
  props: {
    namespace: { String, required: true },
    cussolId: { String, required: true },
  },
  emits: ['apply:solution'],
  data() {
    this.tcrud_conf = {
      getEndpoint: item => `poker/custom-solutions/${item.id}/`,
      getBatchDeleteEndpoint: item => `poker/custom-solutions/batch-delete/`,
      getChannelId: () => {
        let channelId = this.$store.getters.bc_channels_ids.find(id => id.includes('floating_gmf'))
        channelId =
          channelId ||
          (this.$route.query.channelId?.includes('floating_gmf')
            ? this.$route.query.channelId
            : null)
        return channelId
      },
      deleteSuccessMessage: 'messages.sucess_saved_solutions',
      deleteErrorMessage: 'messages.error_deleting_saved_solutions',
      idKey: 'id',
    }
    this.tbl_persistSortKey = 'customsolutions_sort'

    this.tcrud_endpoint = 'poker/custom-solutions/'
    this.tcrud_mutation = 'api_poker_custom_solutions_table'
    this.tcrud_apiVersion = 'v1'
    this.tcrud_channelId = 'floating_gmf'
    return {
      // last_used, name, ordering_position
      tbl_sortConf: {
        sortKey: 'last_used',
        sortOrder: 'desc',
      },
      selectedRows: [],
      lastTouchedIdx: -1,
      detailItem: null,

      tcrud_limit: 100,
      tcrud_offset: 0,
    }
  },
  created() {
    this.tbl_setSortConf()
    this.tcrud_getDataCheck()
  },
  beforeUnmount() {
    this.$store.commit('api_reset', { key: this.tcrud_mutation })
    this.$store.dispatch('api_cancel_pending_requests', {
      callIds: [this.tcrud_endpoint],
    })
  },
  methods: {
    applyItem(item) {
      this.$emit('apply:solution', item)
    },
    handleChangeFilter() {
      this.tcrud_offset = 0
      if (this.$refs.tablebody) {
        this.$refs.tablebody.scrollTop(0)
      }
    },
    handleScroll: throttle(function (e, scroller) {
      if (this.tcrud_loading || !this.tcrud_hasNext || !scroller) return
      const isBottom = scroller.scrollTop + scroller.offsetHeight > scroller.scrollHeight - 10
      if (isBottom) {
        this.tcrud_offset += this.tcrud_limit
        this.tcrud_getDataCheck({ mergeData: true })
      }
    }, 20),
    toggleFavorite(item) {
      const expected_item = {
        ...item,
        is_favorite: !item.is_favorite,
      }
      this.tcrud_patchItem({
        item,
        patchData: { is_favorite: !item.is_favorite },
        expected_item,
      })
    },
    patchName({ item, name }) {
      const expected_item = {
        ...item,
        name,
      }
      this.tcrud_patchItem({
        item,
        patchData: { name },
        expected_item,
      })
    },
    openCustomSolutionsDialog() {
      const tmpNamespace = 'tmp/primary'
      const dialog = `solfigr-create-custom-solution-dialog_namespace-${tmpNamespace}_mode-keepboard`
      this.$store.dispatch('router_set_dialogs', dialog)
    },
  },
  computed: {
    allTags() {
      return this.$store.getters.tags_get_all_tags('custom_solutions')
    },
    thead() {
      const cols = {
        name: {
          name: this.$t('table_head.name'),
          sortKey: 'name',
          width: 14,
        },
        positions: {
          name: this.$t('table_head.positions'),
          width: 8,
        },
        board: {
          name: this.$t('table_head.board'),
          width: 8,
        },
        tags: {
          name: this.$t('table_head.tags'),
          width: 16,
        },
        actions: {
          name: this.$t('table_head.actions'),
          minWidth: 14,
        },
      }
      const res = {}
      Object.keys(cols).forEach(key => {
        const col = cols[key]
        const width = col.width || col.minWidth
        res[key] = {
          ...col,
          cssStyle: !width
            ? ''
            : `
            --min-width-th: ${width}rem;
            ${col.minWidth ? '' : `--max-width-th: ${width}rem`};
            `,
          tdStyle: !width
            ? ''
            : `
            --min-width-td: ${width}rem;
            ${col.minWidth ? '' : `--max-width-td: ${width}rem`};
            `,
        }
      })
      return res
    },
    tcrud_apiData() {
      return this.$store.getters.api_get_data(this.tcrud_mutation)
    },
    tcrud_itemsDrag() {
      let res = this.tcrud_apiData?.results || []
      res = res.filter(i => i.is_favorite) // to hide after unfavoriting
      return res
    },
    tcrud_items() {
      const res = this.tcrud_apiData?.results || []
      return res
    },
    tcrud_hasNext() {
      return !!this.tcrud_apiData?.next
    },
    tcrud_apiCall() {
      return this.$store.getters.api_get(this.tcrud_mutation)
    },
    tcrud_loading() {
      return this.tcrud_apiCall?.loading
    },
    tcrud_error() {
      return this.tcrud_apiCall?.error
    },
    filter() {
      return this.$store.getters.ffcs
    },
    tcrud_params() {
      if (!this.filter.loaded) return {}
      return {
        ...(this.filter.params || {}),
        // [tags_key]: validTags,
        limit: this.tcrud_limit,
        offset: this.tcrud_offset,
        ordering: `${this.tbl_sortConf.sortOrder === 'desc' ? '-' : ''}${
          this.tbl_sortConf.sortKey
        }`,
      }
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },
    globals_action() {
      return this.$store.getters.globals_action
    },
  },
}
</script>

<style lang="scss" scoped>
.tblcustomsols {
  display: flex;
  min-height: 0;
  flex-direction: column;
  flex: 1;
}
</style>
